import localStorageService from '../../utils/services/localStorage/localStorage';
import { DebugTools } from './debugToolTypes';
import type { DebugToolState } from './debugToolTypes';

export const DEBUG_TOOL_PERSISTED_SETTINGS_STORAGE_KEY = 'milanote.debug.toolbar.persist';
export const DEBUG_TOOL_PERSISTENCE_ENABLED_STORAGE_KEY = 'milanote.debug.toolbar.persist-enabled';

/**
 * Determines whether debug-tool updates are persisted between sessions.
 */
export const getDebugToolPersistenceEnabled = (): boolean =>
    localStorageService.getItem(DEBUG_TOOL_PERSISTENCE_ENABLED_STORAGE_KEY) === 'true';

/**
 * Sets whether subsequent debug-tool updates are persisted between sessions.
 */
export const setDebugToolPersistenceEnabled = (enabled: boolean): void => {
    localStorageService.setItem(DEBUG_TOOL_PERSISTENCE_ENABLED_STORAGE_KEY, enabled.toString());
};

/**
 * Clears all persisted debug-tool settings without changing the persistence preference.
 */
export const clearPersistedDebugToolSettings = (): void => {
    localStorageService.removeItem(DEBUG_TOOL_PERSISTED_SETTINGS_STORAGE_KEY);
};

/**
 * Reads the persisted debug-tool settings, treating invalid stored values as empty settings.
 */
export const getPersistedDebugToolSettings = (): DebugToolState => {
    try {
        const storedSettings = localStorageService.getItem(DEBUG_TOOL_PERSISTED_SETTINGS_STORAGE_KEY);
        if (!storedSettings) return {};

        const parsedSettings = JSON.parse(storedSettings);
        if (!parsedSettings || typeof parsedSettings !== 'object') return {};

        return parsedSettings as DebugToolState;
    } catch (error) {
        return {};
    }
};

/**
 * Persists a debug-tool update when persistence is enabled.
 */
export const persistDebugToolUpdate = ({
    toolName,
    ...updates
}: { toolName: DebugTools } & Record<string, unknown>): void => {
    if (!getDebugToolPersistenceEnabled()) return;

    const persistedSettings = getPersistedDebugToolSettings();
    const updatedSettings = {
        ...persistedSettings,
        [toolName]: {
            ...persistedSettings[toolName],
            ...updates,
        },
    };

    localStorageService.setItem(DEBUG_TOOL_PERSISTED_SETTINGS_STORAGE_KEY, JSON.stringify(updatedSettings));
};
